import { useMutation, useQueryClient, type QueryKey } from '@tanstack/react-query';
import { METHOD, sendJSONRequest } from '@/state/http-request.helpers.ts';
import { useAuth } from '@clerk/clerk-react';
import { GET_MEDIA_QUERY_KEY } from '@/state/queries/media/use-get-media.ts';
import { GET_EVENT_SESSIONS_QUERY_KEY } from '@/state/queries/sessions/use-event-sessions.ts';
import { GET_EVENT_FILTERED_SESSIONS_QUERY_KEY } from '@/state/queries/sessions/use-filtered-event-sessions.ts';
import { GET_EVENT_FLOWS_QUERY_KEY } from '@/state/queries/flows/use-get-event-flows.ts';
import { GET_FLOW_QUERY_KEY } from '@/state/queries/flows/use-get-flow.ts';
import { GET_SESSION_QUERY_KEY } from '@/state/queries/sessions/use-get-session.ts';
import type { ApproveMediaAssetDto } from '@/types/dtos/media/request/approve-media-asset.dto.ts';
import { GET_CONTRIBUTOR_SUMMARY_QUERY_KEY } from '@/state/queries/contributors/user-contributors.ts';
import type { FlowDto } from '@/types/dtos/flows/response/flow.dto.ts';
import type { SessionDto } from '@/types/dtos/sessions/session.dto';
import { getObjectId } from '@/helpers/object.id.helper';
import type { MediaParentEntity } from '@/types/dtos/media/media-parent-entity.enum';
import {
  patchMediaAssetApprovalInQueryCache,
  type MediaAssetApprovalPatch,
} from '@/state/mutations/media/patch-media-asset-approval-cache.ts';
import { useFlowStore } from '@/state/stores/use-flow-store';
import { invalidateSimulationCueSheet } from '@/state/queries/player/use-get-simulation-cue-sheet';
import { useEventStore } from '@/state/stores/use-event-store';

type ApproveMediaAssetParams = ApproveMediaAssetDto & {
  parentId?: string;
  parentType?: MediaParentEntity;
};

type ApproveMediaAssetOptimisticContext = {
  previousQueryEntries: Array<[QueryKey, unknown]>;
  previousFlowStore: {
    flow?: FlowDto;
    flowPlan?: FlowDto;
    flowOrganize?: FlowDto;
  };
  previousEventStore: {
    eventSessions?: SessionDto[];
    eventSessionsPlan?: SessionDto[];
    eventSessionsOrganize?: SessionDto[];
  };
};

function resolveApproveParent(
  queryClient: ReturnType<typeof useQueryClient>,
  variables: ApproveMediaAssetParams,
): { parentId?: string; parentType?: string } {
  let parentId: string | undefined = variables.parentId;
  let parentType: string | undefined = variables.parentType;

  if (parentId && parentType) {
    return { parentId, parentType };
  }

  const mediaCache = queryClient.getQueriesData({ queryKey: [GET_MEDIA_QUERY_KEY] });
  for (const [, mediaData] of mediaCache) {
    if (mediaData && typeof mediaData === 'object' && 'pages' in mediaData) {
      const pages = Array.isArray((mediaData as { pages?: unknown[] }).pages)
        ? (mediaData as { pages: unknown[] }).pages
        : [];
      for (const page of pages) {
        if (page && typeof page === 'object' && 'data' in page) {
          const pageData = (page as { data: unknown }).data;
          const media = Array.isArray(pageData) ? pageData : [pageData];
          const foundMedia = media.find(
            (m): m is { id: string; parent?: { id: string }; parentType?: string } =>
              m && typeof m === 'object' && 'id' in m && m.id === variables.mediaId,
          );
          if (foundMedia) {
            parentId ??= foundMedia.parent?.id;
            parentType ??= foundMedia.parentType;
            if (parentId && parentType) {
              return { parentId, parentType };
            }
          }
        }
      }
    }
  }

  return { parentId, parentType };
}

function buildApprovalPatch(
  queryClient: ReturnType<typeof useQueryClient>,
  variables: ApproveMediaAssetParams,
): MediaAssetApprovalPatch {
  const { parentId, parentType } = resolveApproveParent(queryClient, variables);
  const parentTypeLower = parentType?.toLowerCase();
  return {
    mediaId: variables.mediaId,
    assetVersion: variables.assetVersion,
    isApproved: variables.isApproved,
    sessionOriginId: parentTypeLower === 'session' && parentId ? parentId : undefined,
  };
}

function snapshotApprovalCaches(queryClient: ReturnType<typeof useQueryClient>): ApproveMediaAssetOptimisticContext {
  const flowState = useFlowStore.getState();
  const eventState = useEventStore.getState();
  return {
    previousQueryEntries: [
      ...queryClient.getQueriesData({ queryKey: [GET_FLOW_QUERY_KEY] }),
      ...queryClient.getQueriesData({ queryKey: [GET_EVENT_FLOWS_QUERY_KEY] }),
      ...queryClient.getQueriesData({ queryKey: [GET_SESSION_QUERY_KEY] }),
      ...queryClient.getQueriesData({ queryKey: [GET_EVENT_SESSIONS_QUERY_KEY] }),
      ...queryClient.getQueriesData({ queryKey: [GET_MEDIA_QUERY_KEY] }),
    ],
    previousFlowStore: {
      flow: flowState.flow,
      flowPlan: flowState.flowPlan,
      flowOrganize: flowState.flowOrganize,
    },
    previousEventStore: {
      eventSessions: eventState.eventSessions,
      eventSessionsPlan: eventState.eventSessionsPlan,
      eventSessionsOrganize: eventState.eventSessionsOrganize,
    },
  };
}

function restoreApprovalCaches(
  queryClient: ReturnType<typeof useQueryClient>,
  context: ApproveMediaAssetOptimisticContext,
) {
  for (const [queryKey, data] of context.previousQueryEntries) {
    queryClient.setQueryData(queryKey, data);
  }
  useFlowStore.setState(context.previousFlowStore);
  useEventStore.setState(context.previousEventStore);
}

function invalidateAfterApproval(queryClient: ReturnType<typeof useQueryClient>, variables: ApproveMediaAssetParams) {
  const { parentId, parentType } = resolveApproveParent(queryClient, variables);
  const parentTypeLower = parentType?.toLowerCase();

  queryClient.invalidateQueries({ queryKey: [GET_MEDIA_QUERY_KEY] });
  queryClient.invalidateQueries({ queryKey: [GET_EVENT_FILTERED_SESSIONS_QUERY_KEY] });
  queryClient.invalidateQueries({ queryKey: [GET_EVENT_FLOWS_QUERY_KEY] });
  queryClient.invalidateQueries({ queryKey: [GET_CONTRIBUTOR_SUMMARY_QUERY_KEY] });
  void invalidateSimulationCueSheet(queryClient);

  if (parentTypeLower === 'session' && parentId) {
    queryClient.invalidateQueries({ queryKey: [GET_SESSION_QUERY_KEY, parentId] });
    queryClient.refetchQueries({ queryKey: [GET_SESSION_QUERY_KEY, parentId], type: 'active' });
    queryClient.invalidateQueries({ queryKey: [GET_EVENT_SESSIONS_QUERY_KEY] });
    queryClient.refetchQueries({ queryKey: [GET_EVENT_SESSIONS_QUERY_KEY], type: 'active' });
  } else if (!parentId) {
    queryClient.invalidateQueries({ queryKey: [GET_SESSION_QUERY_KEY] });
    queryClient.invalidateQueries({ queryKey: [GET_EVENT_SESSIONS_QUERY_KEY] });
  }

  if (parentTypeLower === 'flow') {
    if (parentId) {
      queryClient.invalidateQueries({ queryKey: [GET_FLOW_QUERY_KEY, parentId] });
      queryClient.refetchQueries({ queryKey: [GET_FLOW_QUERY_KEY, parentId], type: 'active' });
    }
    queryClient.invalidateQueries({ queryKey: [GET_FLOW_QUERY_KEY] });
    queryClient.refetchQueries({ queryKey: [GET_FLOW_QUERY_KEY], type: 'active' });
  } else if (parentTypeLower === 'session' && parentId) {
    const sessionId = parentId;
    const flowQueries = queryClient.getQueriesData({ queryKey: [GET_FLOW_QUERY_KEY] });
    const flowsToInvalidate = new Set<string>();

    for (const [queryKey, flowData] of flowQueries) {
      const flow = Array.isArray(flowData) ? flowData[0] : flowData;
      if (flow && typeof flow === 'object' && 'slots' in flow) {
        const flowDto = flow as FlowDto;
        const hasSession = flowDto.slots?.some((slot) => {
          if (slot.contentType !== 'Session') return false;
          const slotSessionId =
            typeof slot.content === 'string'
              ? slot.content
              : slot.content?.origin
                ? getObjectId(slot.content.origin)
                : slot.content?.id;
          return slotSessionId === sessionId || slotSessionId === parentId;
        });

        if (hasSession) {
          const flowId =
            Array.isArray(queryKey) && queryKey.length > 1
              ? queryKey[1]
              : flowDto.id || flowDto.origin
                ? getObjectId(flowDto.origin)
                : undefined;
          if (flowId && typeof flowId === 'string') {
            flowsToInvalidate.add(flowId);
          }
        }
      }
    }

    const eventFlowsQueries = queryClient.getQueriesData({ queryKey: [GET_EVENT_FLOWS_QUERY_KEY] });
    for (const [, flowsData] of eventFlowsQueries) {
      const flows = Array.isArray(flowsData) ? flowsData : [];
      for (const flow of flows) {
        if (flow && typeof flow === 'object' && 'slots' in flow) {
          const flowDto = flow as FlowDto;
          const hasSession = flowDto.slots?.some((slot) => {
            if (slot.contentType !== 'Session') return false;
            const slotSessionId =
              typeof slot.content === 'string'
                ? slot.content
                : slot.content?.origin
                  ? getObjectId(slot.content.origin)
                  : slot.content?.id;
            return slotSessionId === sessionId || slotSessionId === parentId;
          });

          if (hasSession) {
            const flowId = flowDto.id || (flowDto.origin ? getObjectId(flowDto.origin) : undefined);
            if (flowId) {
              flowsToInvalidate.add(flowId);
            }
          }
        }
      }
    }

    flowsToInvalidate.forEach((flowId) => {
      queryClient.invalidateQueries({ queryKey: [GET_FLOW_QUERY_KEY, flowId] });
      queryClient.refetchQueries({ queryKey: [GET_FLOW_QUERY_KEY, flowId], type: 'active' });
    });

    if (flowsToInvalidate.size === 0) {
      queryClient.invalidateQueries({ queryKey: [GET_FLOW_QUERY_KEY] });
      queryClient.refetchQueries({ queryKey: [GET_FLOW_QUERY_KEY], type: 'active' });
    }
  } else {
    queryClient.invalidateQueries({ queryKey: [GET_FLOW_QUERY_KEY] });
    queryClient.refetchQueries({ queryKey: [GET_FLOW_QUERY_KEY], type: 'active' });
  }
}

export const useApproveMediaAssetMutation = () => {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: ApproveMediaAssetParams) => {
      const token = await getToken();
      return approveMediaAsset(params, token ?? '');
    },
    onMutate: async (variables) => {
      await Promise.all([
        queryClient.cancelQueries({ queryKey: [GET_MEDIA_QUERY_KEY] }),
        queryClient.cancelQueries({ queryKey: [GET_FLOW_QUERY_KEY] }),
        queryClient.cancelQueries({ queryKey: [GET_EVENT_FLOWS_QUERY_KEY] }),
        queryClient.cancelQueries({ queryKey: [GET_SESSION_QUERY_KEY] }),
        queryClient.cancelQueries({ queryKey: [GET_EVENT_SESSIONS_QUERY_KEY] }),
      ]);

      const context = snapshotApprovalCaches(queryClient);
      patchMediaAssetApprovalInQueryCache(queryClient, buildApprovalPatch(queryClient, variables));
      return context;
    },
    onError: (_error, _variables, context) => {
      if (context) {
        restoreApprovalCaches(queryClient, context);
      }
    },
    onSuccess: (_data, variables) => {
      // Re-apply in case a concurrent refetch raced past onMutate.
      patchMediaAssetApprovalInQueryCache(queryClient, buildApprovalPatch(queryClient, variables));
      invalidateAfterApproval(queryClient, variables);
    },
  });
};

const approveMediaAsset = async ({ mediaId, assetVersion, isApproved }: ApproveMediaAssetParams, token: string) => {
  const url = `${import.meta.env.VITE_API_URL}/media/${mediaId}/asset/${assetVersion}/approve`;
  return sendJSONRequest({ url, method: METHOD.PATCH, body: { isApproved }, token });
};
